import type { QueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import { QueryCacheKey } from "../../api/queryClient";

import type { ToastProps } from "./Toast";
import { useLoadDashboardVersionMutation } from "./webDashboard";

interface UseSuperAdminDashboardVersionsParams {
  dashboardId: string | undefined;
  queryClient: QueryClient;
  setToastProps: (props: ToastProps | null) => void;
}

export interface UseSuperAdminDashboardVersionsResult {
  versionS3Url: string | null;
  versionS3Timestamp: string | null;
  loadVersion: (s3Url: string, timestamp: Date) => Promise<void>;
  resetToCurrentVersion: () => void;
  isLoadingVersion: boolean;
}

export function useSuperAdminDashboardVersions({
  dashboardId,
  queryClient,
  setToastProps,
}: UseSuperAdminDashboardVersionsParams): UseSuperAdminDashboardVersionsResult {
  const loadDashboardVersionMutation = useLoadDashboardVersionMutation();
  const [versionS3Url, setVersionS3Url] = useState<string | null>(null);
  const [versionS3Timestamp, setVersionS3Timestamp] = useState<string | null>(null);

  const loadVersion = useCallback(
    async (s3Url: string, timestamp: Date) => {
      if (!dashboardId) {
        return;
      }
      try {
        const result = await loadDashboardVersionMutation.mutateAsync({
          s3Url,
          webDashboardId: dashboardId,
        });
        if (result?.web_dashboard_json?.content_config) {
          queryClient.setQueryData(
            [QueryCacheKey.WEB_DASHBOARD, dashboardId],
            (oldData: unknown) => ({
              ...(oldData as object),
              content_config: result.web_dashboard_json.content_config,
            })
          );
          setVersionS3Url(s3Url);
          setVersionS3Timestamp(timestamp.toISOString());
        } else {
          setToastProps({
            message: "Could not load dashboard version: invalid version data",
            type: "error",
            materialIcon: "error",
            onClose: () => setToastProps(null),
          });
        }
      } catch {
        setToastProps({
          message: "Failed to load dashboard version",
          type: "error",
          materialIcon: "error",
          onClose: () => setToastProps(null),
        });
      }
    },
    [dashboardId, queryClient, setToastProps, loadDashboardVersionMutation]
  );

  const resetToCurrentVersion = useCallback(() => {
    setVersionS3Url(null);
    setVersionS3Timestamp(null);
    if (dashboardId) {
      queryClient.invalidateQueries([QueryCacheKey.WEB_DASHBOARD, dashboardId]);
    }
  }, [dashboardId, queryClient]);

  return {
    versionS3Url,
    versionS3Timestamp,
    loadVersion,
    resetToCurrentVersion,
    isLoadingVersion: loadDashboardVersionMutation.isLoading,
  };
}
